import { useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ClockIcon,
  UserGroupIcon,
  GlobeAltIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

// TypeScript interfaces
interface TreatmentType {
  id: string;
  name: string;
  description?: string;
  duration?: number; // in minutes
  cost?: string;
  treatment_code?: string;
  category?: string;
  color: string;
  allowed_in_group: boolean;
  available_online: boolean;
  created_at: string;
  updated_at: string;
}

interface TreatmentFormData {
  name: string;
  description: string;
  duration: string;
  category: string;
  color: string;
  allowed_in_group: boolean;
  available_online: boolean;
}

function TreatmentTypesClient() {
  const { t } = useTranslation(['appointments', 'common']);
  const [treatmentTypes, setTreatmentTypes] = useState<TreatmentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState<TreatmentType | null>(null);
  const [formData, setFormData] = useState<TreatmentFormData>({
    name: '',
    description: '',
    duration: '',
    category: '',
    color: '#3b82f6',
    allowed_in_group: true,
    available_online: true
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Load treatment types
  useEffect(() => {
    loadTreatmentTypes();
  }, []);

  const loadTreatmentTypes = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/appointments/treatment-types/', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTreatmentTypes(data.treatment_types || []);
      } else {
        console.error('Failed to load treatment types');
      }
    } catch (error) {
      console.error('Error loading treatment types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTreatment(null);
    setFormData({
      name: '',
      description: '',
      duration: '',
      category: '',
      color: '#3b82f6',
      allowed_in_group: true,
      available_online: true
    });
    setShowModal(true);
  };

  const handleEdit = (treatment: TreatmentType) => {
    setEditingTreatment(treatment);
    setFormData({
      name: treatment.name,
      description: treatment.description || '',
      duration: treatment.duration?.toString() || '',
      category: treatment.category || '',
      color: treatment.color || '#3b82f6',
      allowed_in_group: treatment.allowed_in_group,
      available_online: treatment.available_online
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert(t('treatmentTypes.treatmentNameRequired'));
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        duration: formData.duration ? parseInt(formData.duration) : null,
        category: formData.category.trim(),
        color: formData.color,
        allowed_in_group: formData.allowed_in_group,
        available_online: formData.available_online
      };

      const url = editingTreatment 
        ? `/api/appointments/treatment-types/${editingTreatment.id}/update/`
        : '/api/appointments/treatment-types/create/';
      
      const method = editingTreatment ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await loadTreatmentTypes();
        setShowModal(false);
        setEditingTreatment(null);
      } else {
        alert(data.error || t('treatmentTypes.failedToSave'));
      }
    } catch (error) {
      console.error('Error saving treatment type:', error);
      alert(t('treatmentTypes.failedToSave'));
    }
  };

  const handleDelete = async (treatment: TreatmentType) => {
    if (!confirm(`Are you sure you want to delete "${treatment.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/appointments/treatment-types/${treatment.id}/delete/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await loadTreatmentTypes();
      } else {
        alert(data.error || t('treatmentTypes.failedToDelete'));
      }
    } catch (error) {
      console.error('Error deleting treatment type:', error);
      alert(t('treatmentTypes.failedToDelete'));
    }
  };

  const filteredTreatments = treatmentTypes.filter(treatment =>
    treatment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (treatment.description && treatment.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (treatment.category && treatment.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDuration = (minutes?: number) => {
    if (!minutes) return t('treatmentTypes.notSet');
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-6 border border-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-card-foreground">{t('treatmentTypes.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('treatmentTypes.subtitle')}
            </p>
          </div>
          <button 
            onClick={handleCreate}
            className="btn-primary"
          >
            <PlusIcon className="h-5 w-5" />
            <span>{t('treatmentTypes.addTreatment')}</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 max-w-md">
          <input
            type="text"
            placeholder={t('treatmentTypes.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredTreatments.length} treatment{filteredTreatments.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Treatment Types Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading treatment types...</div>
        </div>
      ) : filteredTreatments.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-muted-foreground mb-4">
            {searchTerm ? t('treatmentTypes.noSearchResults') : t('treatmentTypes.noTreatments')}
          </div>
          {!searchTerm && (
            <button onClick={handleCreate} className="btn-primary">
              <PlusIcon className="h-5 w-5" />
              <span>{t('treatmentTypes.createFirst')}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTreatments.map((treatment) => (
            <div key={treatment.id} className="bg-card rounded-xl border border-border p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div 
                      className="w-4 h-4 rounded-full border border-border flex-shrink-0" 
                      style={{ backgroundColor: treatment.color }}
                      title={`Color: ${treatment.color}`}
                    />
                    <h3 className="text-lg font-semibold text-card-foreground">{treatment.name}</h3>
                  </div>
                  {treatment.category && (
                    <p className="text-sm text-primary font-medium mb-1">{treatment.category}</p>
                  )}
                  {treatment.description && (
                    <p className="text-sm text-muted-foreground">{treatment.description}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(treatment)}
                    className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                    title={t('treatmentTypes.editTreatment')}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(treatment)}
                    className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                    title={t('treatmentTypes.deleteTreatment')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {/* Duration */}
                <div className="flex items-center space-x-3">
                  <ClockIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <span className="text-sm text-muted-foreground">Duration: </span>
                    <span className="text-sm font-medium text-card-foreground">
                      {formatDuration(treatment.duration)}
                    </span>
                  </div>
                </div>

                {/* Group Sessions */}
                <div className="flex items-center space-x-3">
                  <UserGroupIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <span className="text-sm text-muted-foreground">Group sessions: </span>
                    <span className={`text-sm font-medium ${
                      treatment.allowed_in_group ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {treatment.allowed_in_group ? 'Allowed' : 'Not allowed'}
                    </span>
                  </div>
                </div>

                {/* Online Booking */}
                <div className="flex items-center space-x-3">
                  <GlobeAltIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <span className="text-sm text-muted-foreground">Online booking: </span>
                    <span className={`text-sm font-medium ${
                      treatment.available_online ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {treatment.available_online ? 'Available' : 'Not available'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card rounded-xl border border-border w-full max-w-2xl my-8 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-border flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-card-foreground">
                  {editingTreatment ? 'Edit Treatment Type' : 'Add Treatment Type'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-muted-foreground" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Treatment Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="e.g., Dental Cleaning, Root Canal..."
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Optional description of the treatment..."
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="e.g., Consultation, Cleaning, Surgery"
                />
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Color
                </label>
                <div className="flex items-center space-x-3 mb-3">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-12 h-10 border border-border rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="#3b82f6"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
                
                {/* Predefined Colors */}
                <div className="flex flex-wrap gap-2 mb-2">
                  {[
                    '#3b82f6', // Blue
                    '#ef4444', // Red
                    '#10b981', // Green
                    '#f59e0b', // Amber
                    '#8b5cf6', // Purple
                    '#ec4899', // Pink
                    '#06b6d4', // Cyan
                    '#84cc16', // Lime
                    '#f97316', // Orange
                    '#6366f1', // Indigo
                    '#14b8a6', // Teal
                    '#f43f5e', // Rose
                  ].map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-lg border-2 transition-all ${
                        formData.color === color 
                          ? 'border-card-foreground scale-110 shadow-md' 
                          : 'border-border hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Color used for displaying this treatment type in the calendar and forms
                </p>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Default Duration (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="e.g., 30, 60, 90..."
                />
              </div>

              {/* Group Sessions */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="allowed_in_group"
                  checked={formData.allowed_in_group}
                  onChange={(e) => setFormData({ ...formData, allowed_in_group: e.target.checked })}
                  className="h-4 w-4 text-primary border-border rounded focus:ring-primary/20"
                />
                <label htmlFor="allowed_in_group" className="text-sm font-medium text-card-foreground">
                  Allow in group sessions
                </label>
              </div>

              {/* Available Online */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="available_online"
                  checked={formData.available_online}
                  onChange={(e) => setFormData({ ...formData, available_online: e.target.checked })}
                  className="h-4 w-4 text-primary border-border rounded focus:ring-primary/20"
                />
                <label htmlFor="available_online" className="text-sm font-medium text-card-foreground">
                  Available for online booking
                </label>
              </div>

              </div>

              {/* Actions - Sticky Footer */}
              <div className="p-6 border-t border-border flex-shrink-0 bg-card">
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    <CheckIcon className="h-4 w-4" />
                    <span>{editingTreatment ? 'Update' : 'Create'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(TreatmentTypesClient);
